import React, { useState, useCallback, useEffect } from 'react';

// Constants
import ColorConstants from 'Wpds/colors/ColorConstants';

// Utils
import { getCurrentMoment, convertMomentToUnix } from 'Shared/utils/DateTime.utils';

// Hooks
import { useSmac } from 'Shared/utils/hooks/use-smac';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useBaProfile } from 'Shared/utils/hooks/use-ba-profile';
import { useTrackPiSuggestedTopics } from 'Shared/tracking/use-track-pi-suggested-topics';

// Components
import { WpCollapsible } from 'Wpds/components/collapsible';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpIcon } from 'Wpds/components/icon';
import { WpText } from 'Wpds/components/text';
import PiSuggestedTopicsNoInspireAccessState from './EmptyState/PiSuggestedTopicsNoInspireAccessState';
import PiSuggestedTopicsMissingBAState from './EmptyState/PiSuggestedTopicsMissingBAState';
import PiSuggestedTopicsList from './PiSuggestedTopicsList';
import { WpButton } from 'Wpds/components/button';
import AgendaAi from './AgendaAi/AgendaAi';
import PiSuggestedTopicsCategoryDropdown from './PiSuggestedTopicsCategoryDropdown';
import { WpPopover, WpPopoverContent, WpPopoverToggle } from 'Wpds/components/popover';
import { Illustrations, WpIllustration } from 'Wpds/components/illustration';

// Styles
import './PiSuggestedTopics.scss';

const PiSuggestedTopics = () => {
  const { hasInspireAccess } = useSmac();
  const { user, isPiUser, getFlag, updateFlag } = useCurrentUser();

  const flagName = 'seen_pi_suggested_topics_intro';
  const hasSeenIntroPopover = Boolean(getFlag({ flag: flagName }));

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const { otherUserInOneOnOne, isOneOnOne, isGroup, isPersonal } = useWorkspace();
  const [selectedCategory, setSelectedCategory] = useState(isOneOnOne && isPiUser ? 'all' : 'agendaAI');
  const { trackTogglePanelOpen } = useTrackPiSuggestedTopics();

  const baseClassName = 'PiSuggestedTopics';

  const { referencePatternName: currentUserReferencePatternName, isFetching: isFetchingCurrentUserBa } = useBaProfile({
    user,
  });
  const { referencePatternName: otherUserReferencePatternName, isFetching: isFetchingOtherUserBa } = useBaProfile({
    user: otherUserInOneOnOne,
  });

  const handleButtonClick = useCallback(() => {
    trackTogglePanelOpen({ open: !isOpen });
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleUpdatFlag = useCallback(() => {
    updateFlag({ flag: flagName, value: convertMomentToUnix(getCurrentMoment()) });
  }, []);

  const handleCloseBtnClick = useCallback(() => {
    setIsPopoverOpen(false);
    updateFlag({ flag: flagName, value: convertMomentToUnix(getCurrentMoment()) });
  }, []);

  const handleContainerClick = useCallback(() => {
    if (!isOpen) {
      trackTogglePanelOpen({ open: !isOpen });
      setIsOpen(!isOpen);
      if (!hasSeenIntroPopover) {
        handleUpdatFlag();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!hasSeenIntroPopover && isOneOnOne && isPiUser) {
      setTimeout(() => setIsPopoverOpen(true), 100);
    }
  }, []);

  if (isFetchingCurrentUserBa || isFetchingOtherUserBa) return null;

  // edge case: the other user is in an invited state and we can't do much with PI suggested topics
  if (isOneOnOne && !otherUserInOneOnOne) return null;

  const content = (() => {
    if (selectedCategory === 'agendaAI' || !isOneOnOne || !isPiUser)
      return (
        <AgendaAi
          categoryDropdown={
            <PiSuggestedTopicsCategoryDropdown
              selectedCategory={selectedCategory}
              handleSelectCategory={setSelectedCategory}
              showPiCategories={
                hasInspireAccess && !!currentUserReferencePatternName && !!otherUserReferencePatternName
              }
              showAgendaAiOnly={isGroup || isPersonal || !isPiUser}
            />
          }
        />
      );
    if (!hasInspireAccess)
      return (
        <PiSuggestedTopicsNoInspireAccessState
          categoryDropdown={
            <PiSuggestedTopicsCategoryDropdown
              selectedCategory={selectedCategory}
              handleSelectCategory={setSelectedCategory}
              showPiCategories={false}
            />
          }
        />
      );
    if (!currentUserReferencePatternName || !otherUserReferencePatternName)
      return (
        <PiSuggestedTopicsMissingBAState
          categoryDropdown={
            <PiSuggestedTopicsCategoryDropdown
              selectedCategory={selectedCategory}
              handleSelectCategory={setSelectedCategory}
              showPiCategories={false}
            />
          }
        />
      );
    else
      return (
        <PiSuggestedTopicsList
          currentUser={user}
          otherUser={otherUserInOneOnOne}
          category={selectedCategory}
          handleSetCategory={setSelectedCategory}
        />
      );
  })();

  const markup = (
    <WpContainer className={baseClassName} onClick={!isOpen && handleContainerClick}>
      <WpContainerBody>
        <div className={`${baseClassName}__title-bar`}>
          <div className={`${baseClassName}__title-bar__title`}>
            <WpIcon name="lightbulb-on" size="lg" color={ColorConstants.blue[500]} />
            <WpText noMargin bold>
              Suggested Topics
            </WpText>
          </div>
          <div>
            <WpButton onClick={handleButtonClick} variant="tertiary">
              <WpIcon name={isOpen ? 'chevron-up' : 'chevron-down'} />
            </WpButton>
          </div>
        </div>
        <WpCollapsible open={isOpen}>{content}</WpCollapsible>
      </WpContainerBody>
    </WpContainer>
  );

  if (!hasSeenIntroPopover) {
    return (
      <WpPopover
        open={isPopoverOpen}
        placement="bottom"
        withBoxShadow
        onOpenChange={handleCloseBtnClick}
        withPadding={false}
        dismisssOnOutsidePress={false}
      >
        <WpPopoverToggle>{markup}</WpPopoverToggle>
        {!isOpen && (
          <WpPopoverContent>
            <div className={`${baseClassName}__intro-popover`}>
              <div className={`${baseClassName}__intro-popover__header`}>
                <div className={`${baseClassName}__intro-popover__header__btn-wrapper`}>
                  <WpButton variant="tertiary" size="xs" onClick={handleCloseBtnClick}>
                    <WpIcon name="times" />
                  </WpButton>
                </div>
                <WpText variant="bodyMd" bold noMargin>
                  New!
                </WpText>
                <WpIllustration src={Illustrations.pi_suggested_topic_example} />
              </div>
              <div className={`${baseClassName}__intro-popover__footer`}>
                <WpText tag="h5" variant="bodyXxl">
                  PI-suggested topics
                </WpText>
                <WpText>Personalized agenda topics designed to strengthen your relationship.</WpText>
              </div>
            </div>
          </WpPopoverContent>
        )}
      </WpPopover>
    );
  } else return <>{markup}</>;
};

export default PiSuggestedTopics;
